import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api } from '../api/client'
import type { Project, ProjectDetail } from '../api/types'
import { VENDOR_LABEL } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { usePolled } from '../lib/usePolled'
import { useAuth } from '../lib/auth'
import { checksStale } from '../lib/health'
import { MONITORING_DELAYED, statusTone, statusWord, systemStatus } from '../lib/freshness'
import { canEdit, cardStatus, filterProjects, projectTiles, roleLabel, tabFrom } from '../lib/projects'
import { sourceDetail } from '../lib/stationSource'
import { initials } from '../components/UserMenu'
import { StationAlarms } from '../components/Alarms'
import { ProjectChat } from '../components/ProjectChat'
import { ProjectLogs } from '../components/ProjectLogs'
import { ProjectImages } from '../components/ProjectImages'
import { ProjectDocuments } from '../components/ProjectDocuments'
import { SensorDocs } from '../components/SensorDocs'

const GRID: Record<number, string> = {
  32638: 'WGS 84 / UTM 38N (EPSG:32638)',
  32637: 'WGS 84 / UTM 37N (EPSG:32637)',
  4326: 'WGS 84 (EPSG:4326)',
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

/** The Projects page (the Projects board, Overview tab): the reader's
 *  projects on the left, every project for a platform admin, and the chosen
 *  one's stations and people on the right, with its tabs. */
export function Projects() {
  const { user } = useAuth()
  const platformAdmin = user?.role === 'admin'
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const list = useAsync(() => api.projects.list(), [])
  const all = list.data?.projects ?? []
  const shown = filterProjects(all, query)
  const asked = Number(params.get('selected'))
  const selectedId = all.some((p) => p.id === asked) ? asked : shown[0]?.id
  const detail = useAsync(
    () => (selectedId ? api.projects.get(selectedId) : Promise.resolve(undefined)), [selectedId])

  const pick = (id: number) => setParams({ selected: String(id) }, { replace: true })
  // Checks that stopped cannot vouch for a station, on a card as on Home.
  const stale = list.data ? checksStale(list.data.health_checked_at) : false

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="sr-only">Projects</h1>
          <p className="muted">
            Each project groups stations and people. A station can sit in more than one project.
          </p>
        </div>
        <div className="pj-head-controls">
          <div className="search-field">
            <input type="search" className="search-input" aria-label="Search projects"
                   placeholder="Search projects" value={query} autoComplete="off"
                   onChange={(event) => setQuery(event.target.value)} />
          </div>
          {platformAdmin && <Link className="button primary" to="/projects/new">New project</Link>}
        </div>
      </div>

      {list.error && <p className="error">{list.error}</p>}
      {list.loading && !list.data && <p className="muted">Loading…</p>}

      {list.data && (
        <div className="pj-layout">
          <section className="pj-list" aria-labelledby="pj-list-head">
            <h2 id="pj-list-head">
              {platformAdmin ? 'All Projects' : 'Your Projects'}{' '}
              <span className="muted">({shown.length})</span>
            </h2>
            {shown.map((p) => (
              <ProjectCard key={p.id} project={p} selected={p.id === selectedId} stale={stale}
                           onPick={() => pick(p.id)} />
            ))}
            {!all.length && (
              <p className="pj-empty">
                {platformAdmin
                  ? <>No projects yet. <Link to="/projects/new">Create the first project</Link></>
                  : 'You are not in any project yet. A project admin adds people.'}
              </p>
            )}
            {all.length > 0 && !shown.length && (
              <p className="pj-empty">
                {platformAdmin
                  ? 'No project matches. Clear the search or create a new project.'
                  : 'No project matches. Clear the search.'}
              </p>
            )}
            <p className="muted pj-foot">Stations in no project still show on Home.</p>
          </section>

          {selectedId !== undefined && (
            <ProjectOverview detail={detail.data} loading={detail.loading} error={detail.error}
                             platformAdmin={platformAdmin} />
          )}
        </div>
      )}
    </div>
  )
}

function ProjectCard({ project, selected, stale, onPick }: {
  project: Project; selected: boolean; stale: boolean; onPick: () => void
}) {
  const status = cardStatus(project, stale)
  return (
    <button type="button" className="pj-card" aria-pressed={selected} onClick={onPick}>
      <span className="pj-card-name">{project.name}</span>
      <span className="pj-card-facts">
        <span>{plural(project.station_count, 'station')}</span>
        <span>{plural(project.member_count, 'member')}</span>
        <span>{project.timezone}</span>
      </span>
      <ProjectAlarmPill counts={project.alarms} />
      {status.noData.length > 0 && (
        <span className="pj-card-nodata">No data: {status.noData.join(', ')}</span>
      )}
      {status.notChecked && (
        <span className="pj-card-notchecked">{MONITORING_DELAYED}: the health checks have stopped</span>
      )}
    </button>
  )
}

/** The card's pill (the Projects board): its open alarms or warnings. */
function ProjectAlarmPill({ counts }: { counts?: { alarm: number; warning: number } }) {
  if (!counts) return null
  if (counts.alarm) return <span className="pill status-alarm">{plural(counts.alarm, 'alarm')}</span>
  if (counts.warning) return <span className="pill status-warning">{plural(counts.warning, 'warning')}</span>
  return <span className="pill status-stale">No alarm</span>
}

function ProjectOverview({ detail, loading, error, platformAdmin }: {
  detail: ProjectDetail | undefined; loading: boolean; error?: string; platformAdmin: boolean
}) {
  if (error) return <section className="pj-panel"><p className="error pj-pad">{error}</p></section>
  if (loading || !detail) {
    return <section className="pj-panel"><p className="muted pj-pad">Loading…</p></section>
  }
  const { project, my_role: role, stations, members } = detail
  return <Overview detail={detail} platformAdmin={platformAdmin} key={project.id}
                   role={role} stations={stations} members={members} />
}

function Overview({ detail, platformAdmin, role, stations, members }: {
  detail: ProjectDetail; platformAdmin: boolean; role: ProjectDetail['my_role']
  stations: ProjectDetail['stations']; members: ProjectDetail['members']
}) {
  const { project } = detail
  const alarms = usePolled(() => api.alerts.alarms({ project: project.id }), [project.id], 30_000)
  // Chat, Station Cameras (`images` in the address, which older links hold),
  // Documents and Sensor Docs: the tab is in the address, so a link can open
  // any; the chat polls, the documents reload after a change.
  const [params, setParams] = useSearchParams()
  const tab = tabFrom(params)
  const setTab = (next: string, image?: string) => setParams((was) => {
    const out = new URLSearchParams(was)
    if (next === 'overview') out.delete('tab'); else out.set('tab', next)
    if (image) out.set('image', image); else out.delete('image')
    return out
  }, { replace: true })
  const messages = usePolled(() => api.projects.messages(project.id), [project.id], 15_000)
  const images = useAsync(() => api.projects.images(project.id), [project.id])
  const documents = useAsync(() => api.projects.documents(project.id), [project.id])
  const sensorDocs = useAsync(() => api.projects.sensorDocs(project.id), [project.id])
  const selectedImage = params.get('image')
  const chatCount = messages.data ? `${messages.data.messages.length}${messages.data.more ? '+' : ''}` : '…'
  const stale = checksStale(stations[0]?.health_checked_at ?? null)
  const tiles = projectTiles(stations, stale)
  return (
    <section className="pj-panel" aria-labelledby="pj-sel">
      <div className="pj-panel-head">
        <div>
          <h2 id="pj-sel">{project.name}</h2>
          <span className="pj-panel-facts">
            {project.client && <span>{project.client}</span>}
            <span>Time zone {project.timezone}</span>
            <span>Grid {GRID[project.crs_epsg]}</span>
            <span>No data after 3× a station's expected interval</span>
          </span>
          <span className="pj-panel-role">
            {platformAdmin
              ? 'You manage every project as a platform admin.'
              : `You are ${role === 'admin' ? 'an' : 'a'} ${roleLabel(role)} in this project.`}
          </span>
        </div>
        {canEdit(role) && (
          <Link className="button chart-tool" to={`/projects/${project.id}/edit`}>Edit project</Link>
        )}
      </div>

      <div role="tablist" aria-label="Project view" className="pj-tabs">
        {([['overview', 'Overview', ''], ['chat', 'Chat', chatCount], ['logs', 'Logs', ''],
           ['images', 'Station Cameras', images.data ? String(images.data.images.length) : '…'],
           ['documents', 'Project documents', documents.data ? String(documents.data.documents.length)
             : documents.error ? '!' : '…'],
           ['sensor-docs', 'Sensor documents', sensorDocs.data ? String(sensorDocs.data.docs.length)
             : sensorDocs.error ? '!' : '…']] as const)
          .map(([key, label, count]) => (
            <button key={key} type="button" role="tab" className="pj-tab" aria-selected={tab === key}
                    onClick={() => setTab(key)}>
              {label}{count && <span className="count"> {count}</span>}
            </button>
          ))}
      </div>

      {tab === 'logs' && <ProjectLogs projectId={project.id} stations={stations} />}
      {tab === 'chat' && (
        <ProjectChat projectId={project.id} projectName={project.name}
                     messages={messages.data?.messages}
                     onChanged={messages.reload} />
      )}
      {tab === 'images' && (
        <ProjectImages stations={stations.map((st) => ({ code: st.code, name: st.name }))}
                       images={images.data?.images} selected={selectedImage}
                       onSelect={(id) => setTab('images', id ?? undefined)} />
      )}

      {tab === 'documents' && (
        <ProjectDocuments projectId={project.id} canUpload={canEdit(role)}
                          documents={documents.data?.documents} loadError={documents.error}
                          onChanged={documents.reload} />
      )}

      {tab === 'sensor-docs' && (
        <SensorDocs projectId={project.id} list={sensorDocs.data} loadError={sensorDocs.error}
                    onChanged={sensorDocs.reload} />
      )}

      {tab === 'overview' && <>
      {project.description && <p className="pj-pad pj-description">{project.description}</p>}

      <div className="pj-tiles">
        <div><span>Stations</span><strong>{tiles.stations}</strong></div>
        <div><span>Receiving</span><strong className="pj-good">{tiles.receiving}</strong></div>
        <div>
          <span>No data</span>
          <strong className={tiles.noData ? 'pj-bad' : undefined}>{tiles.noData}</strong>
        </div>
        {tiles.notChecked > 0 && (
          <div><span>{MONITORING_DELAYED}</span><strong>{tiles.notChecked}</strong></div>
        )}
      </div>

      <div className="pj-pad pj-alarm-head">
        <h3 className="pj-sub">
          Active Alarms <span className="muted">({alarms.data?.alarms.length ?? '…'})</span>
        </h3>
        {(platformAdmin || canEdit(role)) && (
          <Link to={`/alerts?project=${project.id}`}>Alerts for this project</Link>
        )}
      </div>
      {alarms.data && (alarms.data.alarms.length
        ? <div className="pj-alarms"><StationAlarms alarms={alarms.data.alarms} /></div>
        : <p className="pj-pad muted pj-no-alarm">No alarm is active.</p>)}

      <h3 className="pj-pad pj-sub">Stations <span className="muted">({stations.length})</span></h3>
      <div className="pj-stations">
        <div className="pj-row pj-row-head">
          <span>Station</span><span>Source</span><span>System status</span>
        </div>
        {stations.map((station) => {
          const status = systemStatus(station, stale)
          return (
            <Link key={station.code} className="pj-row"
                  to={`/explore/${station.code}?project=${project.id}`}>
              <span className="st-cell">
                <span className="st-name">{station.name}</span>
                <span className="st-sub mono">{station.code}</span>
              </span>
              <span className="st-cell">
                <span>{VENDOR_LABEL[station.vendor]}</span>
                <span className="st-sub">{sourceDetail(station)}</span>
              </span>
              <span className="st-cell">
                <span className={`ss ss-${statusTone(status)}`}>{statusWord(status)}</span>
                {!station.active && <span className="st-sub">Decommissioned</span>}
              </span>
            </Link>
          )
        })}
      </div>

      <div className="pj-people">
        <h3 className="pj-sub">People <span className="muted">({members.length})</span></h3>
        {members.map((member) => (
          <div key={member.user_id} className="pj-person">
            <span className="pj-avatar" aria-hidden="true">{initials(member.name)}</span>
            <span className="pj-person-name">{member.name}</span>
            <span className="muted">{roleLabel(member.role)}</span>
          </div>
        ))}
      </div>
      </>}
    </section>
  )
}
